import { format, parseISO } from 'date-fns'
import api from '@/http/api'
import type { OrderType } from '@/types'

// The dashboard only ever holds one page of orders, so the export refetches
// the whole list itself. Deliberately no search/tag params: the file is always
// every order in the system.
const PAGE_SIZE = 500

export const fetchAllOrders = async (): Promise<OrderType[]> => {
  const all: OrderType[] = []
  let page = 1
  let totalPages = 1

  do {
    const res = await api.get('/order/all', { params: { page, limit: PAGE_SIZE } })
    const rows: OrderType[] = res.data?.data ?? []
    all.push(...rows)
    totalPages = res.data?.pagination?.totalPages ?? page
    // A malformed pagination block must not spin forever.
    if (!rows.length) break
    page += 1
  } while (page <= totalPages)

  return all
}

type Column = { header: string; value: (o: OrderType) => unknown }

const formatDate = (value?: string | null) => {
  if (!value) return ''
  try {
    const parsed = value.includes('T') ? parseISO(value) : new Date(value.replace(' ', 'T'))
    return isNaN(parsed.getTime()) ? String(value) : format(parsed, 'yyyy-MM-dd HH:mm')
  } catch {
    return String(value)
  }
}

const COLUMNS: Column[] = [
  { header: 'Order ID', value: (o) => o.order_id },
  { header: 'Job Title', value: (o) => o.job_title },
  { header: 'Status', value: (o) => o.status },
  { header: 'Order Type', value: (o) => o.order_type },
  { header: 'Product Type', value: (o) => o.product_type },
  { header: 'Product Name', value: (o) => o.product_name },
  { header: 'Quantity', value: (o) => o.quantity },
  { header: 'Size', value: (o) => o.size },
  { header: 'Metal Type', value: (o) => o.metal_type },
  { header: 'Due Date', value: (o) => formatDate(o.due_date) },
  { header: 'Labels', value: (o) => o.labels },
  { header: 'Special Instructions', value: (o) => o.special_instructions },
  { header: 'Customer Name', value: (o) => o.c_name },
  { header: 'Business Name', value: (o) => o.c_business_name },
  { header: 'Customer ID', value: (o) => o.customer_id },
  { header: 'Created At', value: (o) => formatDate(o.created_at) },
  { header: 'Updated At', value: (o) => formatDate(o.updated_at) },
]

const escapeCell = (value: unknown): string => {
  if (value === null || value === undefined) return '""'
  // `labels` and friends arrive as arrays; flatten them into one cell.
  let raw = Array.isArray(value) ? value.join('; ') : String(value)
  // Excel runs a cell starting with = + - @ (or tab/CR) as a formula. Job
  // titles and instructions are customer-typed, so "=HYPERLINK(...)" would run
  // on the admin's machine. A leading ' makes Excel show it as plain text.
  if (/^[=+\-@\t\r]/.test(raw)) raw = `'${raw}`
  return `"${raw.replace(/"/g, '""')}"`
}

export const toCsv = (orders: OrderType[]): string => {
  const rows = [
    COLUMNS.map((c) => escapeCell(c.header)).join(','),
    ...orders.map((o) => COLUMNS.map((c) => escapeCell(c.value(o))).join(',')),
  ]
  // BOM so Excel reads the file as UTF-8 instead of the local codepage.
  return '\uFEFF' + rows.join('\r\n')
}

export const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const exportOrdersToCsv = async (): Promise<number> => {
  const orders = await fetchAllOrders()
  downloadCsv(toCsv(orders), `orders-${format(new Date(), 'yyyy-MM-dd')}.csv`)
  return orders.length
}
